import { Fragment, useEffect, useState } from "react";
import type { ChangeEntry } from "@spoonity/flags-contract";
import { listEnvironmentChanges } from "../api/endpoints";
import { EmptyState } from "../components/EmptyState";
import { JsonDiff } from "../components/JsonDiff";
import { LoadMore } from "../components/LoadMore";
import { useToast } from "../components/Toast";
import { useWorkspaceEnv } from "../state/WorkspaceEnvContext";

export function AuditLogPage() {
  const { environment } = useWorkspaceEnv();
  const { pushError } = useToast();
  const [entries, setEntries] = useState<ChangeEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [actor, setActor] = useState("");
  const [target, setTarget] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!environment) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const page = await listEnvironmentChanges(environment.id, {
          actor: actor || undefined,
          target: target || undefined,
          limit: 50,
        });
        if (cancelled) return;
        setEntries(page.items);
        setNextCursor(page.nextCursor);
      } catch (err) {
        if (!cancelled) pushError(err, "Could not load audit log");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [environment, actor, target, pushError]);

  async function loadMore() {
    if (!environment || !nextCursor) return;
    setLoading(true);
    try {
      const page = await listEnvironmentChanges(environment.id, {
        actor: actor || undefined,
        target: target || undefined,
        cursor: nextCursor,
        limit: 50,
      });
      setEntries((cur) => [...cur, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      pushError(err, "Could not load audit log");
    } finally {
      setLoading(false);
    }
  }

  if (!environment) return null;

  return (
    <div>
      <div className="row wrap" style={{ marginBottom: "var(--space-4)" }}>
        <input
          className="select"
          placeholder="Filter by actor"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          aria-label="Filter by actor"
        />
        <input
          className="select"
          placeholder="Filter by target (flag/segment key)"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          aria-label="Filter by target"
        />
      </div>

      {entries.length === 0 && !loading ? (
        <EmptyState title="No changes recorded" />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Actor</th>
              <th>Role</th>
              <th>Action</th>
              <th>Target</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <Fragment key={entry.id}>
                <tr>
                  <td>{new Date(entry.createdAt).toLocaleString()}</td>
                  <td>{entry.actor}</td>
                  <td>{entry.actorRole}</td>
                  <td>{entry.action}</td>
                  <td>{entry.target}</td>
                  <td>
                    <button
                      className="link-button"
                      onClick={() => setExpanded((cur) => (cur === entry.id ? null : entry.id))}
                    >
                      {expanded === entry.id ? "Hide" : "Details"}
                    </button>
                  </td>
                </tr>
                {expanded === entry.id && (
                  <tr>
                    <td colSpan={6}>
                      <JsonDiff before={entry.before} after={entry.after} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
      <LoadMore nextCursor={nextCursor} loading={loading} onLoadMore={loadMore} />
    </div>
  );
}
